// @ts-check

/**
 * @file Renders project board filter controls.
 */

import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {createRef, memo, useEffect} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, ScrollView, Text, View} from "react-native-propforge"
import {TextInput} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import Button from "../../../components/button"
import Modal from "../../../components/modal"
import ProjectBoardFilterChip from "./board-filter-chip"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode, RefObject} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {(...args: unknown[]) => unknown} ProjectBoardFiltersCallback */

/**
 * @typedef {object} ProjectBoardFiltersProps
 * @property {string} activeFilter Active filter key.
 * @property {ProjectBoardFiltersCallback} onFilterPress Filter selection callback.
 * @property {ProjectBoardFiltersCallback} onNewColumnPress New-column button callback.
 * @property {ProjectBoardFiltersCallback} onRefreshPress Refresh button callback.
 * @property {ProjectBoardFiltersCallback} onSearchChangeText Search text callback.
 * @property {(visibleStates: string[]) => unknown} onTaskStateVisibilityChange State visibility callback.
 * @property {ProjectBoardFiltersCallback} onTaskStatesButtonPress State-visibility modal open callback.
 * @property {ProjectBoardFiltersCallback} onTaskStatesModalClosePress State-visibility modal close callback.
 * @property {string} searchQuery Current search query.
 * @property {boolean} showTaskStatesModal Whether the state visibility modal is open.
 * @property {{text: string, value: string}[]} taskStateOptions Available task states.
 * @property {string[]} visibleTaskStates State values currently included in board columns.
 */

/** @typedef {Record<string, never>} ProjectBoardFiltersState */

/**
 * Project board filter and search controls.
 * @augments {ShapeComponent<ProjectBoardFiltersProps, ProjectBoardFiltersState>}
 */
class ProjectBoardFilters extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  static propTypes = PropTypesExact({
    activeFilter: PropTypes.string.isRequired,
    onFilterPress: PropTypes.func.isRequired,
    onNewColumnPress: PropTypes.func.isRequired,
    onRefreshPress: PropTypes.func.isRequired,
    onSearchChangeText: PropTypes.func.isRequired,
    onTaskStateVisibilityChange: PropTypes.func.isRequired,
    onTaskStatesButtonPress: PropTypes.func.isRequired,
    onTaskStatesModalClosePress: PropTypes.func.isRequired,
    searchQuery: PropTypes.string.isRequired,
    showTaskStatesModal: PropTypes.bool.isRequired,
    taskStateOptions: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })).isRequired,
    visibleTaskStates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired
  })

  /** @type {RefObject<TextInput | null>} */
  searchInputRef = createRef()

  /** Syncs the search input from route state. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const searchQuery = this.p.searchQuery
    useEffect(() => {
      syncSearchInputValue(this.searchInputRef, searchQuery)
    }, [searchQuery])
  }

  /**
   * Renders board filter controls.
   * @returns {ReactNode} Project board filters content.
   */
  render() {
    const filters = [
      {
        key: "all",
        label: _("All")
      },
      {
        key: "mine",
        label: _("Mine")
      },
      {
        key: "due_soon",
        hasDot: true,
        label: _("Due soon")
      }
    ]
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <View
        style={styles.container ||= {
          paddingHorizontal: 24,
          paddingTop: 16
        }}
        testID="projectBoardScreen/filters"
      >
        <ScrollView
          contentContainerStyle={styles.filterRow ||= {
            alignItems: "center",
            flexDirection: "row",
            paddingBottom: 12
          }}
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.filterScroll ||= {
            flexGrow: 0
          }}
          testID="projectBoardScreen/filters/chips"
        >
          {filters.map((filter) => {
            return (
              <ProjectBoardFilterChip
                filterKey={filter.key}
                hasDot={Boolean(filter.hasDot)}
                isActive={this.p.activeFilter === filter.key}
                key={filter.key}
                label={filter.label}
                onPress={this.p.onFilterPress}
              />
            )
          })}
        </ScrollView>
        <TextInput
          defaultValue={this.p.searchQuery}
          autoCapitalize="none"
          autoCorrect={false}
          onChangeText={this.p.onSearchChangeText}
          placeholder={_("Search tasks...")}
          placeholderTextColor={colors.placeholder}
          ref={this.searchInputRef}
          spellCheck={false}
          style={styles[`searchInput-${themeName}`] ||= {
            backgroundColor: colors.surface,
            borderColor: colors.borderStrong,
            borderRadius: 12,
            borderWidth: 1,
            color: colors.text,
            fontSize: 14,
            minHeight: 44,
            paddingHorizontal: 14,
            paddingVertical: 10
          }}
          testID="projectBoardScreen/filters/searchInput"
        />
        <View
          style={styles.actionsRow ||= {
            alignItems: "center",
            alignSelf: "flex-start",
            flexDirection: "row",
            gap: 12,
            marginTop: 12
          }}
          testID="projectBoardScreen/filters/actions"
        >
          <Button
            iconName="arrows-rotate"
            label={_("Refresh")}
            onPress={this.p.onRefreshPress}
            testID="projectBoardScreen/filters/refreshButton"
            variant="outline"
          />
          <Button
            iconName="filter"
            label={_("Task states")}
            onPress={this.p.onTaskStatesButtonPress}
            testID="projectBoardScreen/filters/taskStatesButton"
            variant="outline"
          />
          <Button
            iconName="plus"
            label={_("New Column")}
            onPress={this.p.onNewColumnPress}
            testID="projectBoardScreen/filters/newColumnButton"
          />
        </View>
        <Modal
          cardStyle={styles.taskStatesModalCard ||= {
            maxWidth: 420
          }}
          isVisible={this.p.showTaskStatesModal}
          onRequestClose={this.p.onTaskStatesModalClosePress}
          testID="projectBoardScreen/taskStatesModal"
        >
          <Text
            style={styles[`taskStatesModalTitle-${themeName}`] ||= {
              color: colors.text,
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 8
            }}
            testID="projectBoardScreen/taskStatesModal/title"
          >
            {_("Visible task states")}
          </Text>
          <Text
            style={styles[`taskStatesModalDescription-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 14,
              lineHeight: 20,
              marginBottom: 16
            }}
            testID="projectBoardScreen/taskStatesModal/description"
          >
            {_("Choose which task states should be shown on this board.")}
          </Text>
          <View
            style={styles.taskStatesList ||= {
              gap: 8
            }}
            testID="projectBoardScreen/taskStatesModal/list"
          >
            {this.p.taskStateOptions.map((stateOption) => {
              const isVisible = this.p.visibleTaskStates.includes(stateOption.value)

              return (
                <Pressable
                  key={stateOption.value}
                  onPress={this.tt.onTaskStateOptionPress(stateOption.value)}
                  style={styles[`taskStateOption-${themeName}-${stateOption.value}-${isVisible}`] ||= {
                    alignItems: "center",
                    backgroundColor: isVisible ? colors.infoBackground : colors.surface,
                    borderColor: isVisible ? colors.info : colors.border,
                    borderRadius: 12,
                    borderWidth: 1,
                    flexDirection: "row",
                    minHeight: 44,
                    paddingHorizontal: 14,
                    paddingVertical: 10
                  }}
                  testID={`projectBoardScreen/taskStatesModal/option/${stateOption.value}`}
                >
                  <View
                    style={styles.taskStateOptionIcon ||= {
                      marginRight: 10,
                      width: 16
                    }}
                    testID={`projectBoardScreen/taskStatesModal/option/${stateOption.value}/icon`}
                  >
                    <FontAwesome6
                      color={isVisible ? colors.link : colors.textMuted}
                      name={isVisible ? "square-check" : "square"}
                      size={15}
                    />
                  </View>
                  <Text
                    style={styles[`taskStateOptionText-${themeName}-${stateOption.value}-${isVisible}`] ||= {
                      color: isVisible ? colors.link : colors.text,
                      fontSize: 14,
                      fontWeight: 700
                    }}
                    testID={`projectBoardScreen/taskStatesModal/option/${stateOption.value}/text`}
                  >
                    {stateOption.text}
                  </Text>
                </Pressable>
              )
            })}
          </View>
          <View
            style={styles.taskStatesModalActions ||= {
              alignItems: "center",
              flexDirection: "row",
              justifyContent: "flex-end",
              marginTop: 18
            }}
            testID="projectBoardScreen/taskStatesModal/actions"
          >
            <Button
              label={_("Done")}
              onPress={this.p.onTaskStatesModalClosePress}
              testID="projectBoardScreen/taskStatesModal/doneButton"
            />
          </View>
        </Modal>
      </View>
    )
  }

  /**
   * Toggles one task state in the visible-state list.
   * @param {string} stateValue Task state to toggle.
   * @returns {() => void} Press handler.
   */
  onTaskStateOptionPress = (stateValue) => () => {
    let visibleStates
    if (this.p.visibleTaskStates.includes(stateValue)) {
      visibleStates = this.p.visibleTaskStates.filter((visibleState) => visibleState !== stateValue)
    } else {
      visibleStates = this.p.taskStateOptions
        .map((stateOption) => stateOption.value)
        .filter((visibleState) => visibleState === stateValue || this.p.visibleTaskStates.includes(visibleState))
    }
    this.p.onTaskStateVisibilityChange(visibleStates)
  }
}

/** @type {NamedExoticComponent<ProjectBoardFiltersProps>} */
const ProjectBoardFiltersComponent = memo(shapeComponent(ProjectBoardFilters))

export default ProjectBoardFiltersComponent

/**
 * Syncs the visible TextInput value to the active route search query.
 * @param {RefObject<TextInput | null>} inputRef Input reference to update.
 * @param {string} searchQuery Search query to display.
 * @returns {void}
 */
function syncSearchInputValue(inputRef, searchQuery) {
  if (!inputRef.current) {
    return
  }
  if (typeof inputRef.current.setNativeProps === "function") {
    inputRef.current.setNativeProps({
      text: searchQuery
    })
    return
  }
  if (!searchQuery && typeof inputRef.current.clear === "function") {
    inputRef.current.clear()
  }
}
